import React, { useMemo } from 'react'

import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import ImageWithReservedSpace from 'components/Common/ImageWithReservedSpace'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import { accountAccessShowLogin } from 'actions/UiActions'
import WalledContent from 'components/Common/WalledContent'
import clsx from 'clsx'
import { getDistanceLabel } from 'lib/customer/recommendationUtils'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import type { DistanceUnit } from 'lib/geo/distanceUnits'
import { useIntl } from 'react-intl'

const ImageBadges = styled.div`
  position: relative;
  top: ${rem(16)};
  left: ${rem(8)};
  height: 0;
  z-index: 1;
`

const ContentTags = styled.div`
  display: flex;

  > * + * {
    margin-left: ${rem(4)};
  }
`

const PriceDetails = styled.div``

const MainDescription = styled.div``

const Section = styled.div`
  margin-top: ${rem(8)};
`

const Root = styled.div`
  min-width: ${rem(220)};

  &.bordered {
    border: 1px solid ${props => props.theme.palette.neutral.default.five};
    border-radius: ${props => props.theme.borderRadius.M};
    overflow: hidden;
    display: flex;
    flex-direction: column;

    ${Section} {
      padding-left: ${rem(20)};
      padding-right: ${rem(20)};
      &:last-child {
        flex: 1;
        display: flex;
        align-items: flex-end;
        padding-bottom: ${rem(20)};
      }
    }
  }
`

const Subsection = styled.div`
  > * + * {
    margin-top: ${rem(4)};
  }
`

const ExternalImg = styled.img`
  height: ${rem(178)};
  width: 100%;
  object-fit: cover;
`

const ExternalContainer = styled.div`
  position: relative;
  width: 100%;
`

const slotFinder = (elements: React.ReactElement | Array<React.ReactElement>) => {
  const nodes = ([] as Array<React.ReactElement>).concat(elements)

  return {
    ImageBadges: nodes.find(child => child.type === ImageBadges),
    ContentTags: nodes.find(child => child.type === ContentTags),
    MainDescription: nodes.find(child => child.type === MainDescription),
    PriceDetails: nodes.find(child => child.type === PriceDetails),
  }
}

interface Props extends Utils.PropsWithDataAttributes {
  className?: string
  image?: App.Image
  externalImageUrl?: string
  location: React.ReactNode
  locationDistance?: number
  locationDistanceFrom?: string
  locationDistanceUnit?: DistanceUnit
  title: string
  subtitle?: React.ReactNode
  walledGarden: boolean
  children: React.ReactElement | Array<React.ReactElement>
  variant?: 'default' | 'bordered'
  imageXSRatio?: string
  imageXSWidth?: string
  imageSMRatio?: string
  imageSMWidth?: string
}

function BaseYouMayAlsoLikeTile({
  className,
  image,
  externalImageUrl,
  location,
  locationDistance,
  locationDistanceFrom,
  locationDistanceUnit,
  title,
  subtitle,
  walledGarden,
  variant = 'default',
  imageXSRatio = '3:2',
  imageXSWidth = '273px',
  imageSMRatio = '3:2',
  imageSMWidth = '273px',
  children,
  ...rest
}: Props) {
  const intl = useIntl()
  const slots = slotFinder(children)
  const dispatch = useAppDispatch()

  const locationText = useMemo(() => {
    if (!locationDistanceFrom || !locationDistance || !locationDistanceUnit) {
      return null
    }
    return getDistanceLabel(locationDistance, locationDistanceFrom, locationDistanceUnit, intl)
  }, [locationDistance, locationDistanceFrom, locationDistanceUnit, intl])

  return (
    <Root {...rest} className={clsx({ bordered: variant === 'bordered' }, className)}>
      {slots.ImageBadges}
      {image?.id && <ImageWithReservedSpace
        id={image.id}
        alt={image.title}
        mobileAspectRatio={imageXSRatio}
        mobileWidth={imageXSWidth}
        tabletAspectRatio={imageSMRatio}
        tabletWidth={imageSMWidth}
      >
      </ImageWithReservedSpace>}
      {!image && externalImageUrl && <ExternalContainer>
        <ExternalImg src={externalImageUrl} alt={title} />
        {slots.ImageBadges}
      </ExternalContainer>}
      <Section>
        <Group gap={4} direction="vertical">
          {slots.ContentTags}
          {locationText && <BodyText variant="small" colour="neutral-three" lineClamp={1}>{locationText}</BodyText>}
          <Caption variant="large" weight="bold" lineClamp={1}>{location}</Caption>
        </Group>
        <Subsection>
          <BodyText variant="large" weight="bold" lineClamp={subtitle ? 1 : 3} as="h4">{title}</BodyText>
          {subtitle && <BodyText variant="medium" lineClamp={2} as="h4">{subtitle}</BodyText>}
          {slots.MainDescription}
        </Subsection>
      </Section>
      {slots.PriceDetails && (
        <Section>
          <WalledContent
            enforced={walledGarden}
            wall={(
              <TextLink
                onClick={(e) => {
                  e.preventDefault()
                  dispatch(accountAccessShowLogin())
                }}
              >
                Become a member to see rates
              </TextLink>
            )}
          >
            {slots.PriceDetails}
          </WalledContent>
        </Section>
      )}
    </Root>
  )
}

BaseYouMayAlsoLikeTile.ImageBadges = ImageBadges
BaseYouMayAlsoLikeTile.ContentTags = ContentTags
BaseYouMayAlsoLikeTile.PriceDetails = PriceDetails
BaseYouMayAlsoLikeTile.MainDescription = MainDescription

export default BaseYouMayAlsoLikeTile
